import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useBranding } from '../context/BrandingContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { isNative, pickOrCapturePhoto, hapticSuccess } from '../native';
import {
  Palette, Upload, Loader2, Image as ImageIcon,
  Check, Camera
} from 'lucide-react';

export const BrandingSettings = () => {
  const { user } = useAuth();
  const { branding, updateBranding, uploadLogo, loading } = useBranding();
  const [form, setForm] = useState({
    primary_color: branding.primary_color || '#3b82f6',
    accent_color: branding.accent_color || '#f59e0b',
    app_name: branding.app_name || 'Road to Glory',
    custom_domain: branding.custom_domain || ''
  });
  const fileInputRef = useRef(null);

  const handleSave = async () => {
    const result = await updateBranding(form);
    if (result.success) {
      toast.success('Branding settings saved');
    } else {
      toast.error(result.error);
    }
  };

  const uploadLogoFile = async (file) => {
    if (!file) return;

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be less than 2MB');
      return;
    }

    const result = await uploadLogo(file);
    if (result.success) {
      hapticSuccess();
      toast.success('Logo uploaded successfully');
    } else {
      toast.error(result.error);
    }
  };

  const handleLogoUpload = (e) => uploadLogoFile(e.target.files?.[0]);

  const handleNativeLogoCapture = async () => {
    const file = await pickOrCapturePhoto({ filename: 'logo.jpg' });
    if (file) await uploadLogoFile(file);
  };

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <p className="text-muted-foreground">Please login to manage branding</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8" data-testid="branding-settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Palette className="h-8 w-8" />
          White-Label Branding
        </h1>
        <p className="text-muted-foreground mt-1">
          Customize the look and feel of your platform
        </p>
      </div>

      {/* Logo Upload */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Custom Logo
          </CardTitle>
          <CardDescription>
            Upload your organization logo (PNG, JPEG, WebP, or SVG)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            {/* Logo Preview */}
            <div className="w-24 h-24 rounded-xl border-2 border-dashed border-muted-foreground/30 flex items-center justify-center overflow-hidden">
              {branding.logo_url ? (
                <img 
                  src={`${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}`}
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              ) : (
                <ImageIcon className="h-8 w-8 text-muted-foreground" />
              )}
            </div>
            
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                onChange={handleLogoUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={isNative() ? handleNativeLogoCapture : () => fileInputRef.current?.click()}
                disabled={loading}
                className="gap-2"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isNative() ? (
                  <Camera className="h-4 w-4" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                {isNative() ? 'Add Logo' : 'Upload Logo'}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Recommended: 512x512px, max 2MB
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Color Settings */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Brand Colors
          </CardTitle>
          <CardDescription>
            Customize your platform colors
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Primary Color</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={form.primary_color}
                  onChange={(e) => setForm({...form, primary_color: e.target.value})}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={form.primary_color}
                  onChange={(e) => setForm({...form, primary_color: e.target.value})}
                  placeholder="#3b82f6"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Used for buttons, links, and primary UI elements
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Accent Color</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={form.accent_color}
                  onChange={(e) => setForm({...form, accent_color: e.target.value})}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={form.accent_color}
                  onChange={(e) => setForm({...form, accent_color: e.target.value})}
                  placeholder="#f59e0b"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Used for highlights, badges, and secondary elements
              </p>
            </div>
          </div>
          
          {/* Preview */}
          <div className="p-4 rounded-lg border">
            <p className="text-sm font-medium mb-3">Preview</p>
            <div className="flex gap-3">
              <button 
                className="px-4 py-2 rounded-lg text-white font-medium"
                style={{ backgroundColor: form.primary_color }}
              >
                Primary Button
              </button>
              <button 
                className="px-4 py-2 rounded-lg text-white font-medium"
                style={{ backgroundColor: form.accent_color }}
              >
                Accent Button
              </button>
              <span 
                className="px-3 py-1 rounded-full text-sm font-medium"
                style={{ backgroundColor: `${form.primary_color}20`, color: form.primary_color }}
              >
                Badge
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* App Name & Domain */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Platform Identity</CardTitle>
          <CardDescription>
            Customize your platform name and domain
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>App Name</Label>
            <Input
              value={form.app_name}
              onChange={(e) => setForm({...form, app_name: e.target.value})}
              placeholder="Road to Glory"
            />
            <p className="text-xs text-muted-foreground">
              Displayed in the header and browser tab
            </p>
          </div>
          
          <div className="space-y-2">
            <Label>Custom Domain (Optional)</Label>
            <Input
              value={form.custom_domain}
              onChange={(e) => setForm({...form, custom_domain: e.target.value})}
              placeholder="tournaments.yourdomain.com"
            />
            <p className="text-xs text-muted-foreground">
              Contact support to configure your custom domain
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={loading}
          className="gap-2"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Check className="h-4 w-4" />
          )}
          Save Changes
        </Button>
      </div>
    </div>
  );
};
